import { useEffect, useState } from "react";
import { api, formatApiError as formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/gate-check/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldWarning, Plus, Trash } from "@phosphor-icons/react";

export default function Blacklist() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", phone: "", id_number: "", reason: "" });

    const load = async () => setItems((await api.get("/gate-check/blacklist")).data);
    useEffect(() => { load(); }, []);

    const add = async () => {
        try {
            await api.post("/gate-check/blacklist", form);
            toast.success("Blacklisted");
            setForm({ name: "", phone: "", id_number: "", reason: "" });
            setOpen(false);
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const remove = async (id) => {
        await api.delete(`/gate-check/blacklist/${id}`);
        toast.success("Removed");
        load();
    };

    return (
        <div>
            <PageHeader
                overline="Security"
                title="Blacklist / Watchlist"
                subtitle="Individuals flagged for restricted entry. Attempts to check them in are blocked."
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="btn-open-blacklist-add" className="h-11 bg-brand hover:bg-brand/90 text-white">
                                <Plus size={16} weight="bold" className="mr-2" /> Add to Blacklist
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-display tracking-tight">Add Blacklist Entry</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div>
                                    <Label className="overline text-[10px]">Full Name *</Label>
                                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        data-testid="input-bl-name" className="h-11 mt-1" />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label className="overline text-[10px]">Phone</Label>
                                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                            data-testid="input-bl-phone" className="h-11 mt-1" />
                                    </div>
                                    <div>
                                        <Label className="overline text-[10px]">ID Number</Label>
                                        <Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })}
                                            data-testid="input-bl-id" className="h-11 mt-1" />
                                    </div>
                                </div>
                                <div>
                                    <Label className="overline text-[10px]">Reason *</Label>
                                    <Textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                                        data-testid="input-bl-reason" className="mt-1" />
                                </div>
                                <Button onClick={add} data-testid="btn-add-blacklist"
                                    className="w-full h-11 bg-brand hover:bg-brand/90 text-white">
                                    Add to Blacklist
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                }
            />

            <div className="p-6">
                <Card className="tech-card overflow-hidden">
                    <div className="divide-y divide-border">
                        {items.length === 0 && (
                            <div className="p-8 text-center text-muted-foreground">No blacklisted individuals.</div>
                        )}
                        {items.map((b) => (
                            <div key={b.id} className="px-5 py-4 flex items-center gap-4" data-testid={`row-bl-${b.id}`}>
                                <ShieldWarning size={22} weight="fill" className="text-red-600" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold">{b.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                        Phone: {b.phone || "—"} · ID: {b.id_number || "—"}
                                    </div>
                                    <div className="text-sm mt-1 text-red-700 dark:text-red-400">Reason: {b.reason}</div>
                                </div>
                                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => remove(b.id)}
                                    data-testid={`btn-remove-bl-${b.id}`}>
                                    <Trash size={16} />
                                </Button>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
}
